import cx from 'classnames';
import React, { useEffect, useState } from 'react';

import i18n from 'FRONTEND/utils/i18n';

const CalculatedField = ({
  examType,
  id,
  label,
  fieldsToCompute,
  operation,
  unit,
  clinicalResults,
}) => {
  // String but represents an int.
  const [calculatedScore, setCalculatedScore] = useState('');
  const [interpretation, setInterpretation] = useState(null);

  const getFieldElement = (fieldName) => {
    const escapedFieldName = `consultation-${examType}-${fieldName.trim()}`.replace('/', '\\/');
    const inputs = document.querySelectorAll(`input[name=${escapedFieldName}],select[name=${escapedFieldName}]`);
    const inputType = inputs?.[0].getAttribute('type');

    if (inputType === 'radio') {
      for (const input of inputs) {
        if (input.checked) {
          return input.value;
        }
      }
    } else if (['date', 'number', 'text'].includes(inputType) || inputs[0].nodeName === 'SELECT') {
      return inputs[0].value;
    }
  };

  const onFieldChange = () => {
    const fieldsElements = fieldsToCompute.map((fieldName) => {
      if (fieldName.includes('||')) {
        const conditionalFieldElements = fieldName.split('||').map((fieldName) => getFieldElement(fieldName));
        return conditionalFieldElements.find((fieldElement) => fieldElement !== '' && fieldElement !== null);
      }

      return getFieldElement(fieldName);
    });
    const hasEmptyFields = fieldsElements.some((fieldsElement) => {
      // Some fieldsElement can be undefined
      return fieldsElement == null || fieldsElement === '';
    });

    switch (operation) {
      case 'addition': {
        if (fieldsElements.filter(val => val).length) {
          const result = fieldsElements.filter(val => val)
            .reduce((acc, val) => parseInt(acc) + parseInt(val))
            .toString();
          setCalculatedScore(result);
        } else {
          setCalculatedScore('');
        }
        break;
      }
      case 'bmi': {
        // Fields order: weight, height.
        if (fieldsElements[0] > 0 && fieldsElements[1] > 0) {
          const result = (fieldsElements[0] / Math.pow(fieldsElements[1] / 100, 2)).toFixed(1);
          setCalculatedScore(result);
        } else {
          setCalculatedScore('');
        }
        break;
      }
      case 'division': {
        const result = hasEmptyFields ? '' : fieldsElements.reduce((acc, val) => {
          if (parseInt(val) !== 0) {
            return parseInt(acc) / parseInt(val);
          } else {
            return '';
          }
        }).toString();
        setCalculatedScore(result).toFixed(2);
        break;
      }
      case 'ldl': {
        // Fields order: cholesterol, triglyceride, hdl_chol.
        if (!hasEmptyFields && fieldsElements[0] > 0 && fieldsElements[1] > 0 && fieldsElements[2] > 0) {
          const result = (Math.round((fieldsElements[0] - (fieldsElements[1] / 5) - fieldsElements[2]) * 100) / 100).toString();
          setCalculatedScore(result);
        } else {
          setCalculatedScore('');
        }
        break;
      }
      case 'multiplication': {
        const hasEmptyFields = fieldsElements.some((fieldsElement) => fieldsElement === null || fieldsElement === '');
        const result = hasEmptyFields ? '' : fieldsElements.reduce((acc, val) => {
          return parseInt(acc) * parseInt(val);
        }).toString();
        setCalculatedScore(result);
        break;
      }
      case 'packYears': {
        // Fields order: startDate, endDate, packDay.
        if (fieldsElements[0] && fieldsElements[2] > 0) {
          const startDate = new Date(fieldsElements[0]);
          const endDate = fieldsElements[1] ? new Date(fieldsElements[1]) : new Date();
          // Convert the delta (in milliseconds) to years.
          const timeSmoked = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);
          const result = (timeSmoked >= 0) ? Math.round(timeSmoked * fieldsElements[2]) : '';
          setCalculatedScore(result);
        } else {
          setCalculatedScore('');
        }
        break;
      }
      case 'percentage': {
        if (!hasEmptyFields && fieldsElements[0] > 0 && fieldsElements[1] > 0) {
          const result = ((fieldsElements[0] / fieldsElements[1]) * 100).toFixed(1);
          setCalculatedScore(result);
        } else {
          setCalculatedScore('');
        }
        break;
      }
      case 'ecg_qtc': {
        // Fields order: qt, fc.
        // Provided formula : Qtc = QT + 1.75 * (FC - 60). Results in ms
        if (!hasEmptyFields) {
          const qt = parseFloat(fieldsElements[0]);
          const fc = parseFloat(fieldsElements[1]);
          if (!isNaN(qt) && !isNaN(fc)) {
            const result = (qt + 1.75 * (fc - 60)).toFixed(0);
            setCalculatedScore(result + ' ms');
          } else {
            setCalculatedScore('');
          }
        } else {
          setCalculatedScore('');
        }
        break;
      }
    }
  };

  useEffect(() => {
    onFieldChange();
    fieldsToCompute.forEach((fieldName) => {
      if (fieldName.includes('||')) {
        fieldName.split('||').forEach((conditionalFieldName) => {
          const escapedFieldName = `consultation-${examType}-${conditionalFieldName.trim()}`.replace('/', '\\/');
          document.querySelectorAll(`input[name=${escapedFieldName}],select[name=${escapedFieldName}]`).forEach((input) => {
            if (input.nodeName === 'SELECT') {
              // Select2 doesn't fire native events, only jQuery ones.
              $(`#${escapedFieldName}`).on('change', onFieldChange);
            } else {
              input.addEventListener('input', onFieldChange);
              input.addEventListener('change', onFieldChange);
            }
          });
        });
      } else {
        const escapedFieldName = `consultation-${examType}-${fieldName}`.replace('/', '\\/');
        document.querySelectorAll(`input[name=${escapedFieldName}],select[name=${escapedFieldName}]`).forEach((input) => {
          if (input.nodeName === 'SELECT') {
            $(`#${escapedFieldName}`).on('change', onFieldChange);
          } else {
            input.addEventListener('input', onFieldChange);
            input.addEventListener('change', onFieldChange);
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!clinicalResults) {
      return;
    }
    if (!calculatedScore) {
      setInterpretation(null);
      return;
    }

    const calculatedVal = parseInt(calculatedScore);
    for (const res of clinicalResults) {
      let referenceVal1, referenceVal2;
      if (res.value.includes('-')) {
        const separator = res.value.indexOf('-');
        referenceVal1 = parseInt(res.value.substring(0, separator));
        referenceVal2 = parseInt(res.value.substring(separator + 1));
      } else {
        referenceVal1 = parseInt(res.value);
      }
      if (
        (res.limit === 'lt' && calculatedVal < referenceVal1) ||
        (res.limit === 'lte' && calculatedVal <= referenceVal1) ||
        (res.limit === 'eq' && referenceVal2 && calculatedVal >= referenceVal1 && calculatedVal <= referenceVal2) ||
        (res.limit === 'eq' && calculatedVal === referenceVal1) ||
        (res.limit === 'gte' && calculatedVal >= referenceVal1) ||
        (res.limit === 'gt' && calculatedVal > referenceVal1)
      ) {
        setInterpretation(res);
        break;
      }
    }
  }, [calculatedScore]);

  const renderInterpretation = () => {
    const interpretationCx = cx({
      calculatedField__interpretation: true,
      [`calculatedField__interpretation--${interpretation?.color}`]: true,
    });
    // Capitalize first letter if we didn't in json.
    const result = interpretation.result.replace(/\w/, (c) => c.toUpperCase());
    return (
      <div className='calculatedField__interpretationContainer'>
        <div className='calculatedField__arrow'>⇨</div>
        <div className={interpretationCx}>{result}</div>
      </div>
    );
  };

  return (
    <div className='col-sm-6'>
      <div className='form-group'>
        {label && <label htmlFor={id}>{i18n.localize(label)}</label>}
        {unit ? (
          <div className='input-group'>
            <input id={id} className='form-control' name={id} value={calculatedScore} readOnly />
            {unit && <span className='input-group-addon'>{unit}</span>}
          </div>) : <input id={id} className='form-control' name={id} value={calculatedScore} readOnly />}
        {calculatedScore && interpretation && renderInterpretation()}
      </div>
    </div>
  );
};

export default CalculatedField;
